// Path: ./src/app/pages/NotificationPage/index.tsx
import { Box, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { adminApiService } from "../../../utils/api";
import {
  Notification,
  MuiFilterRequestDto,
} from "@swagger/typescript-fetch-goflyer";

import { GridFilterModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

import { messages } from "../../../locales/messages";
import { useNavigate } from "react-router-dom";
import { TableData } from "../../../utils/interface/tableData";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { useNotificationTableColumns } from "./useNotificationTableColumns";
import {
  SetupPageContainer,
  SetupPageTopAppBar,
} from "../../../utils/commonStyledComponents/commonStyled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { routes } from "../../../utils/constant/navigationRoutes";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";

const TableWrapper = styled.div`
  padding-left: 20px;
  padding-right: 20px;
`;

const NotificationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authHeaders = useAuthHeaders();

  const { paginationModel, sortModel, handleTableChange } = useQueryParams();

  const [paginationLoading, setPaginationLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const [notificationListData, setNotificationListData] =
    useState<TableData<Notification>>();

  const columns = useNotificationTableColumns();

  const fetchNotificationList = async () => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        adminApiService.getManyBaseNotificationAdminControllerNotification(
          undefined,
          undefined,
          undefined,
          undefined,
          sortModel?.length
            ? [`${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`]
            : [`updatedAt,DESC`],
          undefined,
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
        ),
      (NotificationList) => {
        setNotificationListData((prev) => ({
          ...prev,
          rows: NotificationList.data,
          total: NotificationList?.total,
        }));
      },
    );
  };

  const fetchNotificationListWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.notificationAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setNotificationListData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  useEffect(() => {
    setNotificationListData({
      columns: columns,
    });
  }, []);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchNotificationListWithFilter(filterModel);
    } else {
      fetchNotificationList();
    }
  }, [paginationModel, sortModel, filterModel]);

  return (
    <SetupPageContainer data-testid="notificationpage-test-id">
      <AccountCardWrapper title={t(messages.Notifications())}>
        <Box data-testid="view-page">
          <SetupPageTopAppBar>
            <div>
              <Stack direction="row" spacing={2}>
                <PermissionComponentWrapper
                  requiredPermission={["create"]}
                  requiredPermissionGroup={["notification"]}
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <UploadFileIcon
                        style={{ color: "#ff8038", fontSize: 25 }}
                      />
                    }
                    onClick={() => {
                      navigate(routes.notification.create);
                    }}
                    data-testid="create-button"
                  >
                    {t(messages.Create())}
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
              </Stack>
            </div>
          </SetupPageTopAppBar>
          <TableWrapper>
            <ListTableQueryPagination
              loadFailed={listRequestGuard.loadFailed}
              columns={notificationListData?.columns}
              rows={notificationListData?.rows}
              total={paginationLoading ? 0 : notificationListData?.total}
              paginationModel={paginationModel}
              sortModel={sortModel}
              isLoading={paginationLoading}
              onTableChange={handleTableChange}
              dynamicHeightAdjuster={230}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
            />
          </TableWrapper>
        </Box>
      </AccountCardWrapper>
    </SetupPageContainer>
  );
};

export default NotificationPage;
